import { Button, Table } from '@omnipresentgroup/design-system';
import { useLegalEntity } from '@omnipresentgroup/domains';
import type { CutoffReportDayOverride } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { AddCutoffReportDayOverrideModal } from './components';
import { ColumnHeaders, useCutoffReportDayOverridesTableConfig } from './hooks';

const EMPTY_ARRAY: CutoffReportDayOverride[] = [];
const LOADING_ARRAY = Array.from(
  { length: 5 },
  () => ({}) as CutoffReportDayOverride,
);

type Props = {
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnsOverride?: ColumnDef<CutoffReportDayOverride, unknown>[];
  optionsOverride?: Omit<
    TableOptions<CutoffReportDayOverride>,
    'data' | 'columns'
  >;
  legalEntityId: string;
  cutoffReportDayId: string;
};

export const CutoffReportDayOverridesList = ({
  columnVisibility,
  optionsOverride,
  columnsOverride,
  legalEntityId,
  cutoffReportDayId,
}: Props) => {
  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const tableOptions: Omit<
    TableOptions<CutoffReportDayOverride>,
    'data' | 'columns'
  > = useMemo(
    () => ({
      state: {
        columnVisibility: columnVisibilityState,
      },
      getCoreRowModel: getCoreRowModel(),
      onColumnVisibilityChange: setColumnVisibilityState,
      ...optionsOverride,
    }),
    [columnVisibilityState, optionsOverride],
  );

  const { columns } = useCutoffReportDayOverridesTableConfig({
    columnsOverride,
    legalEntityId,
    cutoffReportDayId,
  });

  const { data: legalEntity, isLoading } = useLegalEntity({
    id: legalEntityId,
  });

  const cutoffReportDayOverrides = useMemo(
    () =>
      legalEntity?.cutoffReportDays?.find((day) => day.id === cutoffReportDayId)
        ?.cutoffReportDayOverrides || EMPTY_ARRAY,
    [legalEntity, cutoffReportDayId],
  );

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : cutoffReportDayOverrides,
    columns,
    getRowId: (row: CutoffReportDayOverride) => row.id,
    ...tableOptions,
  });

  return (
    <Table
      id="cutoff-report-day-overrides-list"
      title="Overrides"
      aria-label="overrides"
      table={table}
      loading={isLoading}
      emptyStateProps={{
        id: 'cutoff-report-day-overrides-list-empty-state',
        title: 'No overrides have been set',
      }}
      tableActions={
        columnVisibility?.actions ? (
          <AddCutoffReportDayOverrideModal
            trigger={
              <Button
                variant="secondary"
                icon="Plus"
                size="small"
                disabled={isLoading}
                aria-label="add override"
              >
                Add override
              </Button>
            }
            legalEntityId={legalEntityId}
            cutoffReportDayId={cutoffReportDayId}
          />
        ) : undefined
      }
    />
  );
};
